import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Trash2,
  AlertTriangle,
  Cloud,
  Folder,
  Loader2,
  Github,
  Check,
} from "lucide-react";
import { api, ApiError, type Project } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ProjectSettingsPanelProps {
  open: boolean;
  onClose: () => void;
  project: Project;
  /** Called after the project itself has been deleted (redirect away). */
  onProjectDeleted: () => void;
  /** Called after a partial delete (repo only), so the shell can refetch state. */
  onProjectRefresh?: () => void;
}

const SLIDE_IN = 300;
const SLIDE_OUT = 200;

// ── Modal kinds ──
//
// Single flow: always archives the Daydra project, optionally also
// deletes the repo. Each add-on is opt-in and independently
// recoverable. App deletion isn't part of this panel anymore — the
// CF deploy_destroy tool handles that from chat.

type ModalKind = "project";

// Per-step result for the final run summary
type StepStatus = "ok" | "skipped" | "failed";

interface RunResult {
  repo: StepStatus;
  project: StepStatus;
  errors: string[];
}

export function ProjectSettingsPanel({
  open,
  onClose,
  project,
  onProjectDeleted,
  onProjectRefresh,
}: ProjectSettingsPanelProps) {
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);

  // Which modal is open (null = none)
  const [modal, setModal] = useState<ModalKind | null>(null);

  // Compound-flow selection (project modal only)
  const [alsoDeleteRepo, setAlsoDeleteRepo] = useState(false);

  // Typed confirm string — required when repo deletion is opted in
  const [typedConfirm, setTypedConfirm] = useState("");

  // In-flight run
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RunResult | null>(null);

  const handleClose = useCallback(() => {
    if (closingRef.current) return;
    if (running) return;
    closingRef.current = true;
    setClosing(true);
    setTimeout(() => {
      closingRef.current = false;
      setClosing(false);
      setModal(null);
      setAlsoDeleteRepo(false);
      setTypedConfirm("");
      setError(null);
      setResult(null);
      onClose();
    }, SLIDE_OUT);
  }, [onClose, running]);

  // Escape closes modal first, then panel — blocked during a run
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (running) return;
      if (modal) {
        closeModal();
        return;
      }
      handleClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, handleClose, modal, running]);

  if (!open) return null;

  const hasRepo = !!project.connected_repo;

  function closeModal() {
    if (running) return;
    setModal(null);
    setAlsoDeleteRepo(false);
    setTypedConfirm("");
    setError(null);
    setResult(null);
  }

  // ── Compound project delete ─────────────────────────────────

  async function runProjectDelete() {
    const willDeleteRepo = hasRepo && alsoDeleteRepo;

    const res: RunResult = {
      repo: willDeleteRepo ? "ok" : "skipped",
      project: "ok",
      errors: [],
    };

    setRunning(true);
    setError(null);

    // Order matters:
    //  1. GitHub repo first — permanent on GitHub's side.
    //  2. Daydra project second — also deprovisions the hosted agent.

    if (willDeleteRepo) {
      try {
        await api.github.deleteProjectRepo(project.id);
      } catch (e) {
        res.repo = "failed";
        res.errors.push(
          `GitHub: ${e instanceof ApiError ? e.message : String(e)}`,
        );
      }
    }

    try {
      await api.projects.delete(project.id);
    } catch (e) {
      res.project = "failed";
      res.errors.push(
        `Daydra project: ${e instanceof ApiError ? e.message : String(e)}`,
      );
    }

    setResult(res);
    setRunning(false);

    // Navigate away only when the Daydra project was actually archived
    // AND nothing else failed — a partial failure leaves the user on
    // the panel so they can see what went wrong and retry.
    if (res.project === "ok" && res.errors.length === 0) {
      onProjectDeleted();
    } else if (res.project === "ok") {
      // Project archived but add-ons had issues — refresh so the shell
      // sees the new state, but keep the panel open for the summary.
      onProjectRefresh?.();
    }
  }

  // ── Panel body ──────────────────────────────────────────────

  const panelContent = (
    <div
      className={cn(
        "flex flex-col h-full",
        "w-full",
        "md:w-80",
        "bg-[hsla(var(--surface-hue),var(--surface-sat),12%,0.55)] backdrop-blur-md backdrop-saturate-150",
        "md:border-l md:border-white/[0.06]",
      )}
    >
      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-5 sm:px-6 py-3 pt-4 sm:pt-3 shrink-0 bg-[hsla(var(--surface-hue),var(--surface-sat),9%,0.7)] backdrop-blur-md shadow-[0_1px_12px_rgba(0,0,0,0.3)]">
        <h2 className="text-sm font-medium text-daydra-text truncate flex-1">
          Project Settings
        </h2>
        <button
          onClick={handleClose}
          disabled={running}
          className="flex items-center justify-center h-7 w-7 rounded-lg text-daydra-dim hover:text-daydra-text hover:bg-white/[0.06] transition-colors duration-fast shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Close settings"
        >
          <X size={16} />
        </button>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-6 space-y-5">
        {/* Project summary */}
        <section>
          <h3 className="text-2xs uppercase tracking-widest text-daydra-muted mb-2.5 px-1">
            Project
          </h3>
          <div className="rounded-xl px-3.5 py-3 bg-white/[0.04] border border-white/[0.06]">
            <p className="text-sm font-medium text-daydra-text truncate">
              {project.name}
            </p>
            {project.connected_repo ? (
              <p className="text-2xs text-daydra-muted mt-0.5 truncate">
                {project.connected_repo.repo_full_name}
              </p>
            ) : (
              <p className="text-2xs text-daydra-muted mt-0.5">
                No repo connected
              </p>
            )}
          </div>
        </section>

        {/* Danger zone */}
        <section>
          <h3 className="text-2xs uppercase tracking-widest text-daydra-muted mb-2.5 px-1 flex items-center gap-1.5">
            <AlertTriangle size={11} className="text-daydra-error/70" />
            Danger zone
          </h3>
          <div className="rounded-xl bg-white/[0.04] border border-white/[0.06] overflow-hidden">
            {/* Daydra project — compound. App teardown isn't part of
                this panel anymore — `deploy_destroy` from chat
                handles that. */}
            <DangerRow
              icon={Folder}
              title="Delete project on Daydra"
              onClick={() => {
                setError(null);
                setResult(null);
                setAlsoDeleteRepo(false);
                setTypedConfirm("");
                setModal("project");
              }}
              topBorder={false}
            />
          </div>
        </section>
      </div>
    </div>
  );

  // ── Modals ──────────────────────────────────────────────────

  const modalNode = modal
    ? createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
            onClick={closeModal}
          />
          <div
            className={cn(
              "relative z-10 w-full max-w-md rounded-2xl",
              "bg-[hsla(var(--surface-hue),var(--surface-sat),11%,0.95)] backdrop-blur-xl",
              "border border-white/[0.06] shadow-2xl animate-scale-in",
              "max-h-[90vh] flex flex-col",
            )}
          >
            {(
              <ProjectModal
                project={project}
                hasRepo={hasRepo}
                alsoDeleteRepo={alsoDeleteRepo}
                setAlsoDeleteRepo={setAlsoDeleteRepo}
                typedConfirm={typedConfirm}
                setTypedConfirm={setTypedConfirm}
                running={running}
                error={error}
                result={result}
                onCancel={closeModal}
                onConfirm={runProjectDelete}
              />
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  // ── Mobile: full-screen overlay ──
  const mobileOverlay = createPortal(
    <div className="fixed inset-0 z-50 md:hidden">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => !running && handleClose()}
        style={{
          animation: closing
            ? `fadeOut ${SLIDE_OUT}ms ease-in forwards`
            : `fadeIn ${SLIDE_IN}ms ease-out both`,
        }}
      />
      <div
        className={cn(
          // Full-width wrapper so the panel always fills the mobile viewport
          // regardless of its content's intrinsic width.
          "absolute inset-0 h-full",
          closing ? "animate-slide-out-right" : "animate-slide-in-right",
        )}
      >
        {panelContent}
      </div>
    </div>,
    document.body,
  );

  // ── Desktop: inline right sidebar ──
  const desktopSidebar = (
    <div
      className={cn(
        "hidden md:flex h-full shrink-0 overflow-hidden",
        closing
          ? "animate-sidebar-collapse-right"
          : "animate-sidebar-expand-right",
      )}
    >
      {panelContent}
    </div>
  );

  return (
    <>
      {mobileOverlay}
      {desktopSidebar}
      {modalNode}
    </>
  );
}

// ── Subcomponents ─────────────────────────────────────────────

interface DangerRowProps {
  icon: typeof Trash2;
  title: string;
  /** Optional supporting copy. Omit for rows whose modal explains everything. */
  blurb?: string;
  onClick: () => void;
  disabled?: boolean;
  topBorder?: boolean;
}

function DangerRow({
  icon: Icon,
  title,
  blurb,
  onClick,
  disabled,
  topBorder,
}: DangerRowProps) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        "flex items-center gap-3 w-full px-3.5 py-3 text-left transition-colors duration-fast",
        topBorder && "border-t border-white/[0.04]",
        disabled
          ? "opacity-40 cursor-not-allowed"
          : "hover:bg-white/[0.04]",
      )}
    >
      <Icon
        size={14}
        className={cn("shrink-0 text-daydra-dim", blurb && "mt-0.5 self-start")}
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-daydra-text">{title}</p>
        {blurb && (
          <p className="text-2xs text-daydra-muted mt-0.5 leading-relaxed">
            {blurb}
          </p>
        )}
      </div>
    </button>
  );
}

// ── DO deployment confirm modal ──

// ── Compound project-delete modal ──

interface ProjectModalProps {
  project: Project;
  hasRepo: boolean;

  alsoDeleteRepo: boolean;

  setAlsoDeleteRepo: (v: boolean) => void;

  typedConfirm: string;
  setTypedConfirm: (v: string) => void;
  running: boolean;
  error: string | null;
  result: RunResult | null;
  onCancel: () => void;
  onConfirm: () => void;
}

function ProjectModal({
  project,
  hasRepo,
  alsoDeleteRepo,
  setAlsoDeleteRepo,
  typedConfirm,
  setTypedConfirm,
  running,
  error,
  result,
  onCancel,
  onConfirm,
}: ProjectModalProps) {
  const repoFull = project.connected_repo?.repo_full_name ?? "";
  // `repo_full_name` is "owner/repo"; the typed-confirm should match the
  // bare repo name the user sees on GitHub, not the Daydra project name
  // (which can drift from the repo name over a project's lifetime).
  const repoName = repoFull.includes("/")
    ? repoFull.slice(repoFull.indexOf("/") + 1)
    : repoFull;

  // Repo deletion is the one truly-unrecoverable step (permanent on
  // GitHub's side), so gate it on a typed confirmation.
  const nuclearPhrase = repoName;
  const needsTypedConfirm = alsoDeleteRepo && !!repoName;
  const typedConfirmOk =
    !needsTypedConfirm || typedConfirm.trim() === nuclearPhrase;

  // Two-stage confirm: users land on the configure view, click Delete to
  // move into a final "are you sure" screen that summarizes the full blast
  // radius, and only then does the actual run kick off. This keeps the
  // configure screen scannable while still putting the full consequences
  // directly in front of the user before anything destructive happens.
  const [confirming, setConfirming] = useState(false);

  // Final confirmation view — only shown after the user clicks Delete on
  // the configure screen. Lists the exact resources that will be destroyed
  // and the consequences, so "the big red button" isn't one click away
  // from archiving chat history and nuking a GitHub repo.
  if (confirming && !result) {
    const willDeleteRepo = alsoDeleteRepo && hasRepo;

    return (
      <>
        <div className="flex items-start gap-3 px-5 pt-5 shrink-0">
          <div className="flex items-center justify-center h-9 w-9 rounded-full shrink-0 bg-daydra-error/15">
            <AlertTriangle size={18} className="text-daydra-error" />
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            <h3 className="text-base font-medium text-daydra-text">
              Are you sure?
            </h3>
            <p className="text-xs text-daydra-muted mt-0.5">
              This is the last step. Once confirmed, these actions run
              immediately and can&apos;t be undone.
            </p>
          </div>
        </div>

        <div className="px-5 pt-4 space-y-3 overflow-y-auto">
          <div>
            <p className="text-2xs uppercase tracking-widest text-daydra-muted mb-1.5">
              You are about to delete
            </p>
            <ul className="space-y-1.5">
              <ConsequenceItem>
                <span className="text-daydra-text font-medium">
                  {project.name}
                </span>{" "}
                on Daydra — chat history, sessions, and project memory
                become inaccessible, and the hosted agent is shut down.
              </ConsequenceItem>
              {willDeleteRepo && (
                <ConsequenceItem>
                  The GitHub repository{" "}
                  <span className="font-mono text-daydra-text">{repoFull}</span>{" "}
                  — <strong>permanently</strong>. Forks, issues, PRs, stars,
                  and wiki content are gone for good.
                </ConsequenceItem>
              )}
              
            </ul>
          </div>

          {error && (
            <div className="p-2.5 rounded-lg bg-daydra-error/10 border border-daydra-error/20">
              <p className="text-2xs text-daydra-error leading-relaxed">
                {error}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 px-5 py-5 mt-2 shrink-0">
          <button
            onClick={() => {
              if (running) return;
              setConfirming(false);
            }}
            disabled={running}
            className="flex-1 h-9 rounded-xl bg-daydra-raised text-daydra-text border border-daydra-border text-sm font-medium hover:brightness-125 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Go back
          </button>
          <button
            onClick={onConfirm}
            disabled={running}
            className={cn(
              "flex-1 h-9 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2",
              "bg-daydra-error text-white border border-daydra-error shadow-[0_1px_0_0_rgba(255,255,255,0.08)_inset] hover:bg-daydra-error/90",
              "disabled:opacity-40 disabled:cursor-not-allowed",
            )}
          >
            {running ? (
              <>
                <Loader2 size={13} className="animate-spin" />
                <span>Deleting…</span>
              </>
            ) : (
              <>
                <Trash2 size={13} />
                <span>Delete</span>
              </>
            )}
          </button>
        </div>
      </>
    );
  }

  // Post-run summary view
  if (result) {
    return (
      <>
        <div className="flex items-start gap-3 px-5 pt-5 shrink-0">
          <div
            className={cn(
              "flex items-center justify-center h-9 w-9 rounded-full shrink-0",
              result.errors.length > 0
                ? "bg-daydra-error/15"
                : "bg-daydra-success/15",
            )}
          >
            {result.errors.length > 0 ? (
              <AlertTriangle size={18} className="text-daydra-error" />
            ) : (
              <Check size={18} className="text-daydra-success" />
            )}
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            <h3 className="text-base font-medium text-daydra-text">
              {result.errors.length > 0 ? "Partial cleanup" : "Done"}
            </h3>
            <p className="text-xs text-daydra-muted mt-0.5">
              {result.errors.length > 0
                ? "Some steps didn't complete."
                : "Project and selected resources were deleted."}
            </p>
          </div>
        </div>
        <div className="px-5 pt-4 space-y-1.5 overflow-y-auto">
          <ResultRow label="Daydra project" status={result.project} />
          <ResultRow label="GitHub repository" status={result.repo} />          {result.errors.length > 0 && (
            <div className="mt-2 p-2.5 rounded-lg bg-daydra-error/10 border border-daydra-error/20 space-y-1">
              {result.errors.map((e, i) => (
                <p
                  key={i}
                  className="text-2xs text-daydra-error leading-relaxed"
                >
                  {e}
                </p>
              ))}
            </div>
          )}
        </div>
        <div className="px-5 py-5 mt-2 shrink-0">
          <button
            onClick={onCancel}
            className="w-full h-9 rounded-xl bg-daydra-raised text-daydra-text border border-daydra-border text-sm font-medium hover:brightness-125 transition-all"
          >
            Close
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex items-start gap-3 px-5 pt-5 shrink-0">
        <div className="flex items-center justify-center h-9 w-9 rounded-full shrink-0 bg-daydra-error/10">
          <Folder size={18} className="text-daydra-error/80" />
        </div>
        <div className="flex-1 min-w-0 pt-0.5">
          <h3 className="text-base font-medium text-daydra-text">
            Delete project on Daydra
          </h3>
          <p className="text-xs text-daydra-muted mt-0.5">
            Choose what else to remove. Everything is opt-in.
          </p>
        </div>
      </div>

      <div className="px-5 pt-4 space-y-3 overflow-y-auto">
        {/* Always-on project consequences */}
        <div>
          <p className="text-2xs uppercase tracking-widest text-daydra-muted mb-1.5">
            What always happens
          </p>
          <ul className="space-y-1.5">
            <ConsequenceItem>
              This project is archived on Daydra — all chat history, sessions,
              and memory become inaccessible.
            </ConsequenceItem>
            <ConsequenceItem>
              The hosted agent for this project is shut down.
            </ConsequenceItem>
          </ul>
        </div>

        {/* Add-ons — only render toggles that actually apply, and drop the
            whole section if neither does. No point asking the user to opt
            into deleting resources that don't exist. */}
        {(hasRepo) && (
          <div>
            <p className="text-2xs uppercase tracking-widest text-daydra-muted mb-1.5">
              Add-ons
            </p>
            <div className="space-y-2">
              {hasRepo && (
                <AddonToggle
                  icon={Github}
                  label="Also delete the GitHub repository"
                  sub={repoFull}
                  checked={alsoDeleteRepo}
                  onChange={(v) => {
                    setAlsoDeleteRepo(v);
                    if (!v) setTypedConfirm("");
                  }}
                />
              )}
            </div>
          </div>
        )}

        {/* Typed confirmation (repo deletion only) */}
        {needsTypedConfirm && (
          <div>
            <label className="block text-2xs uppercase tracking-widest text-daydra-muted mb-1.5">
              Repository deletion is permanent — type{" "}
              <span className="font-mono text-daydra-text normal-case">
                {nuclearPhrase}
              </span>{" "}
              to confirm
            </label>
            <input
              type="text"
              autoFocus
              value={typedConfirm}
              onChange={(e) => setTypedConfirm(e.target.value)}
              disabled={running}
              className="w-full h-9 rounded-lg bg-daydra-raised px-3 text-xs font-mono text-daydra-text border border-daydra-border focus:outline-none focus:border-daydra-error/60"
              placeholder={nuclearPhrase}
            />
          </div>
        )}

        {error && (
          <div className="p-2.5 rounded-lg bg-daydra-error/10 border border-daydra-error/20">
            <p className="text-2xs text-daydra-error leading-relaxed">
              {error}
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 px-5 py-5 mt-2 shrink-0">
        <button
          onClick={onCancel}
          disabled={running}
          className="flex-1 h-9 rounded-xl bg-daydra-raised text-daydra-text border border-daydra-border text-sm font-medium hover:brightness-125 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          onClick={() => setConfirming(true)}
          disabled={running || !typedConfirmOk}
          className={cn(
            "flex-1 h-9 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2",
            "bg-daydra-error text-white border border-daydra-error shadow-[0_1px_0_0_rgba(255,255,255,0.08)_inset] hover:bg-daydra-error/90",
            "disabled:opacity-40 disabled:cursor-not-allowed",
          )}
        >
          <Trash2 size={13} />
          <span>Delete</span>
        </button>
      </div>
    </>
  );
}

// ── Tiny shared bits ──

function ConsequenceItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-xs text-daydra-dim leading-relaxed">
      <span className="text-daydra-error/60 mt-0.5 shrink-0">•</span>
      <span>{children}</span>
    </li>
  );
}

interface AddonToggleProps {
  icon: typeof Cloud;
  label: string;
  sub: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}

function AddonToggle({
  icon: Icon,
  label,
  sub,
  checked,
  onChange,
}: AddonToggleProps) {
  return (
    <label
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors duration-fast cursor-pointer",
        checked
          ? "border-daydra-error/50 bg-daydra-error/[0.09]"
          : "border-white/[0.06] bg-white/[0.03] hover:border-white/[0.12]",
      )}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span
        className={cn(
          "flex items-center justify-center h-4 w-4 rounded border shrink-0 transition-colors",
          checked
            ? "bg-daydra-error/20 border-daydra-error/70"
            : "bg-transparent border-white/20",
        )}
      >
        {checked && (
          <Check
            size={11}
            strokeWidth={3}
            className="text-daydra-error"
          />
        )}
      </span>
      <Icon size={13} className="shrink-0 text-daydra-dim" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-daydra-text truncate">{label}</p>
        <p className="text-2xs text-daydra-muted mt-0.5 truncate">{sub}</p>
      </div>
    </label>
  );
}

function ResultRow({
  label,
  status,
}: {
  label: string;
  status: StepStatus;
}) {
  const color =
    status === "ok"
      ? "text-daydra-success"
      : status === "failed"
        ? "text-daydra-error"
        : "text-daydra-muted";
  const text =
    status === "ok" ? "done" : status === "failed" ? "failed" : "skipped";
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-daydra-dim">{label}</span>
      <span className={cn("font-medium", color)}>{text}</span>
    </div>
  );
}
